import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '@/presentation/components/Avatar';
import type { Timelog } from '@/modules/attendance/domain/entities/Timelog';
import type { ThemeTokens } from '@/shared/ui/theme/tokens';
import type { BrandColors } from '@/shared/ui/theme/tokens';
import { formatDayLabel, parsePgDateTime } from '@/shared/utils/datetime';
import { capitalizeIfUpper } from '@/shared/utils/mapping';
import { mapDeviceActionTypeToFuente } from '@/modules/attendance/domain/helpers/deviceActionType';
import { resolveVerificationLabels } from '@/modules/attendance/domain/helpers/verificationMode';
import { VerificationIcon } from './VerificationIcon';

interface Props {
  timelog: Timelog;
  initials: string;
  avatarColor: string;
  onClick: () => void;
  T: ThemeTokens;
  C: BrandColors;
}

export function TimelogListItem({ timelog, initials, avatarColor, onClick, T, C }: Props) {
  const { t } = useTranslation('common');
  const [hov, setHov] = useState(false);

  // datetime stores datetime_pg (server local time) — parse without UTC coercion
  const d = parsePgDateTime(timelog.datetime);
  const isEntrada = timelog.accessType === 1;
  const tipoColor = isEntrada ? C.success : C.danger;
  const tipoLabel = isEntrada ? t('timelog.entry') : t('timelog.exit');
  const timeLabel = d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', hour12: true });
  const sedeLabel = capitalizeIfUpper(timelog.structureCode ?? '—');

  // Resolve method + source labels via canonical lookup table
  const labels = resolveVerificationLabels(timelog.verificationMode, timelog.source ?? null, t);

  // Source (Fuente) column: use resolveVerificationLabels when verificationMode is present,
  // fall back to mapDeviceActionTypeToFuente for legacy timelogs without a verificationMode.
  const hasVerificationMode = timelog.verificationMode != null;
  const fuenteLabel = hasVerificationMode
    ? labels.source
    : mapDeviceActionTypeToFuente(timelog.deviceActionType ?? undefined);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => { setHov(true); }}
      onMouseLeave={() => { setHov(false); }}
      style={{
        display: 'grid',
        gridTemplateColumns: '36px minmax(140px, 1.6fr) minmax(90px, 1fr) minmax(80px, 0.9fr) minmax(80px, 0.9fr)',
        alignItems: 'center',
        gap: 14,
        padding: '12px 16px',
        borderRadius: 11,
        cursor: 'pointer',
        background: hov ? T.rowBg : T.surfaceBg,
        border: `1px solid ${hov ? C.primary + '44' : T.border}`,
        transition: 'all .18s',
        marginBottom: 7,
      }}
    >
      {/* Col 1 — Avatar */}
      <Avatar
        initials={initials}
        color={avatarColor}
        size={34}
        photoUrl={timelog.capturePhotoUrl ?? timelog.employee?.photoUrl ?? null}
      />

      {/* Col 2 — formatDayLabel + tipo·hora */}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {formatDayLabel(d)}
        </div>
        <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 6, background: `${tipoColor}15`, color: tipoColor, borderRadius: 10, padding: '2px 9px', fontSize: 9.5, fontWeight: 700 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: tipoColor }} />
          {tipoLabel} · {timeLabel}
        </div>
      </div>

      {/* Col 3 — Sede */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: 10, minWidth: 0 }}>
        <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
        </svg>
        <span style={{ color: T.text, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sedeLabel}</span>
      </div>

      {/* Col 4 — Método */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: 10 }}>
        <span style={{ display: 'flex', color: T.textMuted }}>
          <VerificationIcon iconKey={labels.methodIconKey} />
        </span>
        <span style={{ color: T.text, fontWeight: 500 }}>{labels.method}</span>
      </div>

      {/* Col 5 — Fuente */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: 10 }}>
        <span style={{ display: 'flex', color: T.textMuted }}>
          {hasVerificationMode ? (
            <VerificationIcon iconKey={labels.sourceIconKey} />
          ) : (
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          )}
        </span>
        <span style={{ color: T.text, fontWeight: 500 }}>{fuenteLabel}</span>
      </div>
    </div>
  );
}
